'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import NumberofTravelers from "@/components/ui/NumberofTravelers"
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext"
import { setTravelersPreferences } from "@/lib/cookies"

type CruiseSearchItem = {
    type: 'category' | 'company' | 'ship';
    name: string;
    icon: string;
}

export default function CruiseSearchForm() {
    const { getVariable } = useLanguageVariables()
    const [cruiseSearchQuery, setCruiseSearchQuery] = useState('')
    const [showCruiseSearchResults, setShowCruiseSearchResults] = useState(false)
    const [selectedMonths, setSelectedMonths] = useState<string[]>([])
    const [showMonthPicker, setShowMonthPicker] = useState(false)
    const cruiseSearchRef = useRef<HTMLDivElement>(null)
    const monthPickerRef = useRef<HTMLDivElement>(null)
    const [travelers, setTravelers] = useState({
        adults: 1,
        children: 0,
        childAges: [] as number[]
    });

    const handleTravelersChange = useCallback((travelersData: { adults: number; children: number; childAges: number[] }) => {
        setTravelers(travelersData);
    }, []);

    // Dışarı tıklandığında kapat
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (cruiseSearchRef.current && !cruiseSearchRef.current.contains(event.target as Node)) {
                setShowCruiseSearchResults(false)
            }
            if (monthPickerRef.current && !monthPickerRef.current.contains(event.target as Node)) {
                setShowMonthPicker(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // 12 ay listesi
    const generateMonthOptions = () => {
        const months = []
        const currentDate = new Date()
        
        for (let i = 0; i < 12; i++) {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1)
            const monthName = date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })
            const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1)
            
            months.push({
                title: capitalizedMonth,
                value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
                id: i + 1
            })
        }
        
        return months
    }

    // Arama verileri - Backend entegrasyonu yapılana kadar boş bırakıldı
    // Production ortamında API'den gelecek verilerle doldurulacaktır
    const cruiseSearchData: CruiseSearchItem[] = [];

    const filteredCruiseResults = cruiseSearchQuery 
        ? cruiseSearchData.filter(item => 
            item.name.toLowerCase().includes(cruiseSearchQuery.toLowerCase())
          )
        : cruiseSearchData

    const toggleMonth = (value: string) => {
        setSelectedMonths(prev => 
            prev.includes(value) 
                ? prev.filter(m => m !== value)
                : [...prev, value]
        )
    }

    const handleSubmit = () => {
        if (!cruiseSearchQuery) {
            alert(`⚠️ ${getVariable('cruise_search_ship_required', 'Please enter ship category, company or ship name!')}`)
            return
        }

        if (selectedMonths.length === 0) {
            alert(`⚠️ ${getVariable('cruise_search_period_required', 'Please select at least one period!')}`)
            return
        }

        // Cookie'ye travelers bilgisini kaydet (oda bazlı yapı)
        setTravelersPreferences({
            product_type: 'ship',
            rooms: [{
                adults: travelers.adults,
                children: travelers.children,
                childAges: travelers.childAges || [],
            }],
        });

        // TODO: Kategori sayfasına yönlendirme
        // Şimdilik alert göster
        alert(getVariable('cruise_search_submitted', 'Cruise search form submitted! Travelers information saved to cookie.'))
        
  
    }

    return (
        <motion.section
            key="panel-3"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{
                duration: 0.15,
                ease: "easeOut"
            }}
            className="rounded-xl bg-white/5 p-2 sm:p-3"
        >
            <motion.div
                className="flex flex-col sm:flex-row items-end gap-2 sm:gap-3"
                initial="hidden"
                animate="visible"
                variants={{
                    hidden: { opacity: 0 },
                    visible: {
                        opacity: 1,
                        transition: {
                            staggerChildren: 0.03,
                            delayChildren: 0
                        }
                    }
                }}
            >
                {/* Akıllı Arama Input */}
                <motion.div
                    ref={cruiseSearchRef}
                    className="w-full relative"
                    variants={{hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 }}}
                >
                    <h3 className="text-alsero-dark-gray text-lg mb-1">{getVariable('cruise_search_ship_title', 'Search Ship')}</h3>
                    <div className="relative">
                        <div className="relative flex flex-row justify-between items-center px-6 py-3 bg-white rounded-lg w-full min-h-[60px]">
                            <div className="flex flex-row gap-2 items-center w-full">
                                <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-alsero-dark-gray">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M9.5 2C5.35786 2 2 5.35786 2 9.5C2 13.6421 5.35786 17 9.5 17C11.0833 17 12.5457 16.4762 13.7266 15.5951L17.2929 19.1614C17.6834 19.5519 18.3166 19.5519 18.7071 19.1614C19.0976 18.7709 19.0976 18.1377 18.7071 17.7472L15.1408 14.1809C16.0219 13 16.5457 11.5376 16.5457 9.95429C16.5457 5.81215 13.188 2.45429 9.04571 2.45429L9.5 2ZM4 9.5C4 6.46243 6.46243 4 9.5 4C12.5376 4 15 6.46243 15 9.5C15 12.5376 12.5376 15 9.5 15C6.46243 15 4 12.5376 4 9.5Z"/>
                                </svg>
                                <input
                                    type="text"
                                    value={cruiseSearchQuery}
                                    onChange={(e) => {
                                        setCruiseSearchQuery(e.target.value)
                                        setShowCruiseSearchResults(true)
                                    }}
                                    onFocus={() => setShowCruiseSearchResults(true)}
                                    placeholder={getVariable('cruise_search_ship_placeholder', 'Category, Company or Ship Name...')}
                                    className="w-full text-alsero-dark-gray text-lg bg-transparent border-none outline-none placeholder:text-alsero-light-gray"
                                />
                            </div>
                        </div>
                        
                        {/* Akıllı Arama Sonuçları */}
                        <AnimatePresence>
                            {showCruiseSearchResults && (
                                <motion.div
                                    initial="closed"
                                    animate="open"
                                    exit="closed"
                                    variants={{
                                        open: {
                                            opacity: 1,
                                            scale: 1,
                                            y: 0,
                                            transition: {
                                                type: "spring",
                                                stiffness: 300,
                                                damping: 24,
                                                staggerChildren: 0.05,
                                                delayChildren: 0.1
                                            }
                                        },
                                        closed: {
                                            opacity: 0,
                                            scale: 0.95,
                                            y: -10,
                                            transition: {
                                                type: "spring",
                                                stiffness: 300,
                                                damping: 24
                                            }
                                        }
                                    }}
                                    className="absolute top-full left-0 right-0 mt-2 z-30 bg-white rounded-xl shadow-xl max-h-[300px] overflow-y-auto"
                                >
                                    {filteredCruiseResults.length > 0 ? (
                                        filteredCruiseResults.map((item, index) => (
                                            <motion.div
                                                key={index}
                                                onClick={() => {
                                                    setCruiseSearchQuery(item.name)
                                                    setShowCruiseSearchResults(false)
                                                }}
                                                className="flex items-center gap-3 px-4 py-3 hover:bg-alsero-soft-white cursor-pointer transition-colors border-b border-alsero-soft-white last:border-b-0"
                                                variants={{
                                                    open: {
                                                        opacity: 1,
                                                        x: 0,
                                                        transition: {
                                                            type: "spring",
                                                            stiffness: 300,
                                                            damping: 24
                                                        }
                                                    },
                                                    closed: {
                                                        opacity: 0,
                                                        x: -10
                                                    }
                                                }}
                                            >
                                                <span className="text-2xl">{item.icon}</span>
                                                <div className="flex flex-col">
                                                    <span className="text-alsero-dark-gray font-medium">{item.name}</span>
                                                    <span className="text-xs text-alsero-light-gray capitalize">
                                                        {item.type === 'category' ? 'Kategori' : item.type === 'company' ? 'Firma' : 'Gemi'}
                                                    </span>
                                                </div>
                                            </motion.div>
                                        ))
                                    ) : (
                                        <motion.div 
                                            className="px-4 py-6 text-center text-alsero-light-gray"
                                            variants={{
                                                open: {
                                                    opacity: 1,
                                                    transition: {
                                                        type: "spring",
                                                        stiffness: 300,
                                                        damping: 24
                                                    }
                                                },
                                                closed: {
                                                    opacity: 0
                                                }
                                            }}
                                        >
                                            {getVariable('cruise_search_no_results', 'No results found')}
                                        </motion.div>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>

                {/* Dönem Seçimi */}
                <motion.div
                    ref={monthPickerRef}
                    className="w-full relative"
                    variants={{hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 }}}
                >
                    <h3 className="text-alsero-dark-gray text-lg mb-1">{getVariable('cruise_search_period_title', 'Select Period')}</h3>
                    <button
                        type="button"
                        onClick={() => setShowMonthPicker(!showMonthPicker)}
                        className="relative flex flex-row justify-between items-center px-6 py-3 bg-white rounded-lg w-full min-h-[60px] cursor-pointer"
                    >
                        <div className="flex flex-row gap-2 items-center">
                            <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-alsero-dark-gray">
                                <path fillRule="evenodd" clipRule="evenodd" d="M18.0348 17.1251C18.0348 18.5221 16.9073 19.6496 15.5102 19.6496H2.94204C1.54499 19.6496 0.41748 18.5221 0.41748 17.1251V4.55692C0.41748 3.15986 1.54499 2.03236 2.94204 2.03236H5.70267V1.1603C5.70409 0.953032 5.77713 0.752628 5.90941 0.593053C6.0417 0.433478 6.22508 0.324548 6.4285 0.284724L6.58353 0.27063C7.06977 0.27063 7.46439 0.64764 7.46439 1.1603V2.03236H10.9879V1.1603C10.9893 0.953032 11.0623 0.752628 11.1946 0.593053C11.3269 0.433478 11.5103 0.324548 11.7137 0.284724L11.8687 0.27063C12.355 0.27063 12.7496 0.64764 12.7496 1.1603V2.03236H15.5102C16.9073 2.03236 18.0348 3.15986 18.0348 4.55692V17.1251ZM2.17921 7.31754V16.7076C2.17921 17.3594 2.70773 17.8879 3.35957 17.8879H15.0927C15.7445 17.8879 16.273 17.3594 16.273 16.7076V7.31754H2.17921Z"/>
                            </svg>
                            <span className="text-alsero-dark-gray text-lg">
                                {selectedMonths.length > 0 
                                    ? `${selectedMonths.length} ${getVariable('cruise_search_periods_selected', 'Periods Selected')}` 
                                    : getVariable('cruise_search_period_title', 'Select Period')}
                            </span>
                        </div>
                        <svg 
                            width="15" 
                            height="8" 
                            viewBox="0 0 15 8" 
                            fill="none" 
                            xmlns="http://www.w3.org/2000/svg"
                            className={`transition-transform duration-200 ${showMonthPicker && 'rotate-180'}`}
                        >
                            <path fillRule="evenodd" clipRule="evenodd" d="M14.3145 0.454593C14.6735 0.796556 14.6735 1.35099 14.3145 1.69295L8.18677 7.53063C7.82782 7.87259 7.24584 7.87259 6.88688 7.53063L0.75913 1.69295C0.400175 1.35099 0.400175 0.796555 0.75913 0.454592C1.11808 0.112629 1.70007 0.112629 2.05902 0.454592C5.12634 3.37671 9.94731 3.37671 13.0146 0.454593C13.3736 0.112629 13.9556 0.112629 14.3145 0.454593Z" fill="#2E2D2D"/>
                        </svg>
                    </button>

                    <AnimatePresence>
                        {showMonthPicker && (
                            <motion.div
                                initial="closed"
                                animate="open"
                                exit="closed"
                                variants={{
                                    open: {
                                        opacity: 1,
                                        scale: 1,
                                        y: 0,
                                        transition: {
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 24,
                                            staggerChildren: 0.05,
                                            delayChildren: 0.1
                                        }
                                    },
                                    closed: {
                                        opacity: 0,
                                        scale: 0.95,
                                        y: -10,
                                        transition: {
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 24
                                        }
                                    }
                                }}
                                className="absolute top-full left-0 mt-2 z-30 rounded-lg bg-white p-6 shadow-xl min-w-[500px]"
                            >
                                <div className="grid grid-cols-3 gap-3">
                                    {generateMonthOptions().map((month) => (
                                        <motion.label
                                            key={month.value}
                                            className="flex items-center gap-2 cursor-pointer group p-3 hover:bg-alsero-soft-white rounded-lg transition-colors"
                                            variants={{
                                                open: {
                                                    opacity: 1,
                                                    x: 0,
                                                    transition: {
                                                        type: "spring",
                                                        stiffness: 300,
                                                        damping: 24
                                                    }
                                                },
                                                closed: {
                                                    opacity: 0,
                                                    x: -10
                                                }
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedMonths.includes(month.value)}
                                                onChange={() => toggleMonth(month.value)}
                                                className="w-4 h-4 min-w-4 min-h-4 flex-shrink-0 rounded border-2 border-alsero-light-gray text-blue-500 cursor-pointer accent-blue-500"
                                            />
                                            <span className="text-md text-alsero-dark-gray group-hover:text-blue-500 transition-colors whitespace-nowrap">
                                                {month.title}
                                            </span>
                                        </motion.label>
                                    ))}
                                </div>
                                {selectedMonths.length > 0 && (
                                    <motion.div 
                                        className="mt-4 pt-4 border-t border-alsero-soft-white"
                                        variants={{
                                            open: {
                                                opacity: 1,
                                                y: 0,
                                                transition: {
                                                    type: "spring",
                                                    stiffness: 300,
                                                    damping: 24
                                                }
                                            },
                                            closed: {
                                                opacity: 0,
                                                y: -10
                                            }
                                        }}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-alsero-light-gray">
                                                {selectedMonths.length} {getVariable('cruise_search_months_selected', 'months selected')}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setSelectedMonths([])
                                                }}
                                                className="text-sm text-blue-500 hover:text-blue-600 font-medium transition-colors"
                                            >
                                                {getVariable('cruise_search_clear_all', 'Clear All')}
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>

                {/* Yolcu Sayısı */}
                <NumberofTravelers 
                    title={getVariable('cruise_search_passengers_title', 'Number of Passengers')} 
                    onChange={handleTravelersChange}
                    icon={
                        <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-alsero-dark-gray">
                            <path d="M15.1811 7.77945C14.7379 9.41938 15.0146 11.6979 16.1374 12.9727C16.3996 13.2703 16.6426 13.5861 16.8643 13.9183C17.7646 15.2672 18.2809 16.8358 18.3576 18.4558C18.3678 18.6042 18.3483 18.7531 18.3002 18.8939C18.252 19.0346 18.1763 19.1643 18.0773 19.2754C17.9784 19.3865 17.8583 19.4767 17.724 19.5407C17.5898 19.6047 17.4441 19.6412 17.2955 19.6482C17.1469 19.6551 16.9984 19.6323 16.8588 19.5811C16.7191 19.5299 16.5911 19.4513 16.4823 19.3499C16.3734 19.2486 16.2859 19.1265 16.2249 18.9908C16.1638 18.8552 16.1305 18.7087 16.1268 18.56C16.0457 16.8394 15.305 15.216 14.0585 14.027C12.8121 12.838 11.1557 12.1747 9.43311 12.1747C7.71052 12.1747 6.05409 12.838 4.80767 14.027C3.56124 15.216 2.82057 16.8394 2.73938 18.56C2.71932 18.8513 2.58602 19.1231 2.36802 19.3173C2.15002 19.5115 1.86465 19.6126 1.57303 19.5989C1.28141 19.5853 1.00671 19.458 0.807769 19.2444C0.608827 19.0307 0.501452 18.7476 0.50864 18.4558C0.585226 16.836 1.10121 15.2675 2.00128 13.9186C2.22286 13.5865 2.46567 13.2708 2.72769 12.9732C3.85043 11.6981 4.12692 9.41954 3.68358 7.77945C3.36429 6.59825 3.4166 5.34736 3.83341 4.19695C4.25022 3.04653 5.01128 2.05243 6.01307 1.34986C7.01487 0.647302 8.20877 0.270386 9.43236 0.270386C10.656 0.270386 11.8499 0.647302 12.8517 1.34986C13.8534 2.05243 14.6145 3.04653 15.0313 4.19695C15.4481 5.34736 15.5004 6.59825 15.1811 7.77945ZM13.156 6.22244C13.156 5.23507 12.7638 4.28814 12.0656 3.58997C11.3674 2.89179 10.4205 2.49956 9.43311 2.49956C8.44574 2.49956 7.49881 2.89179 6.80064 3.58997C6.10247 4.28814 5.71024 5.23507 5.71024 6.22244C5.71024 7.2098 6.10247 8.15673 6.80064 8.8549C7.49881 9.55308 8.44574 9.94531 9.43311 9.94531C10.4205 9.94531 11.3674 9.55308 12.0656 8.8549C12.7638 8.15673 13.156 7.2098 13.156 6.22244Z"/>
                        </svg>
                    } 
                />

                {/* Ara Butonu */}
                <motion.button 
                    onClick={handleSubmit}
                    type="button"
                    className="bg-alsero-first text-lg font-medium rounded-lg text-nowrap text-white px-10 min-h-[60px] cursor-pointer hover:bg-alsero-dark-gray transition-colors duration-300"
                    variants={{hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 }}}
                >
                    {getVariable('cruise_search_button', 'Search Cruise')}
                </motion.button>
            </motion.div>
        </motion.section>
    )
}

